/**
 * 数字存储单位转换函数（支持大数值高精度转换）
 * @param {Object} params - 转换参数
 * @param {number|string} params.value - 数值
 * @param {string} params.unit - 当前单位 ('Byte', 'Ki', 'Mi', 'Gi', 'Ti')
 * @param {string} params.targetUnit - 目标单位 ('Byte', 'Ki', 'Mi', 'Gi', 'Ti')
 * @param {number} params.precision - 保留小数位数，默认0
 * @returns {number} 转换后的数值
 * @throws {Error} 当单位不支持或参数无效时抛出错误
 */
const transUnit = ({
  value,
  unit,
  targetUnit,
  precision = 0,
}: {
  value: number | string;
  unit: string;
  targetUnit: string;
  precision?: number;
}) => {
  // 边界条件处理
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value !== 'number' && typeof value !== 'string') return 0;
  if (!unit || !targetUnit || unit === targetUnit) return Number(value);

  const powBigInt = (base: bigint, exponent: number) => {
    let result = BigInt(1);
    for (let i = 0; i < exponent; i += 1) {
      result *= base;
    }
    return result;
  };

  // 单位列表与对应的字节倍数（以 BigInt 形式存储）
  const units = ['Byte', 'Ki', 'Mi', 'Gi', 'Ti'];
  const one = BigInt(1);
  const base = BigInt(1024);
  const multipliers = [one, base, powBigInt(base, 2), powBigInt(base, 3), powBigInt(base, 4)];

  // 验证单位是否合法
  if (!units.includes(unit)) {
    throw new Error(`不支持的单位: ${unit}，支持的单位: ${units.join(', ')}`);
  }
  if (!units.includes(targetUnit)) {
    throw new Error(`不支持的目标单位: ${targetUnit}，支持的单位: ${units.join(', ')}`);
  }

  // 处理输入值，转换为 BigInt（以最小单位 Byte 的整数倍表示）
  // 经过前置校验，这里 value 一定是 number|string
  const strValue = String(value);
  if (strValue.trim() === '') {
    throw new Error('数值不能为空');
  }

  // 分离整数和小数部分（支持科学计数法，先转为普通数字字符串）
  let normalizedStr;
  if (strValue.includes('e') || strValue.includes('E')) {
    // 科学计数法先转为普通数字，再转为字符串（可能丢失精度，但用于 bigint 转换前）
    // 注意：科学计数法的大数使用 BigInt 直接转换会报错，需先转为普通十进制字符串
    normalizedStr = Number(strValue).toLocaleString('fullwide', { useGrouping: false });
    if (normalizedStr.includes('e')) {
      // 如果 Number 转换后仍然是科学计数法，说明数值太大，直接使用 BigInt 会失败
      // 这种情况下，我们手动解析科学计数法（简单场景，通常大数据会用字符串完整表示）
      throw new Error('对于超大数值，请使用完整十进制字符串表示，避免科学计数法');
    }
  } else {
    normalizedStr = strValue;
  }

  // 检查是否为有效数字格式
  if (!/^-?\d+(\.\d+)?$/.test(normalizedStr)) {
    throw new Error(`无效的数值格式: ${strValue}`);
  }

  // 将数值转换为以 Byte 为单位的 BigInt
  const multiplier = multipliers[units.indexOf(unit)];
  const [integerPart, fractionalPart = ''] = normalizedStr.split('.');

  // 将小数部分转换为整数（最多保留足够精度，这里取最多 15 位小数用于计算）
  // 为了精确转换，我们将数值乘以 multiplier，然后再乘以一个足够大的系数来处理小数
  // 简单方法：使用 BigInt 进行分数运算，但这里为了处理小数，我们将整个值转为以 "最小单位" 的整数倍
  // 更精确的做法：将数值转为分数形式，乘以 multiplier 后取整

  // 采用 BigInt 进行精确计算：value * multiplier
  // 由于 value 可能带小数，我们将 value 表示为整数部分 + 小数部分/10^k
  const fractionalLength = fractionalPart.length;
  const fractionalInt = fractionalPart ? BigInt(fractionalPart) : BigInt(0);
  const integerBig = BigInt(integerPart);

  // 计算总数值（以原始单位表示）的整数倍表示，放大 10^fractionalLength 倍
  const scale = powBigInt(BigInt(10), fractionalLength);
  const valueScaled = integerBig * scale + fractionalInt;

  // 转换为目标单位的缩放值（以 Byte 为单位）
  const targetMultiplier = multipliers[units.indexOf(targetUnit)];
  const sourceMultiplier = multiplier;

  // 计算: (valueScaled * sourceMultiplier) / (targetMultiplier * scale)
  // 使用 BigInt 进行精确运算，最后转换为浮点数并保留精度
  const numerator = valueScaled * sourceMultiplier;
  const denominator = targetMultiplier * scale;

  // 执行整数除法，并获取余数用于小数计算
  const integerResult = numerator / denominator;
  const remainder = numerator % denominator;

  // 计算小数部分（以浮点数形式，保留足够精度）
  let result;
  if (remainder === BigInt(0)) {
    result = Number(integerResult);
  } else {
    // 将余数转为浮点数，除以分母得到小数部分
    const fractional = Number(remainder) / Number(denominator);
    result = Number(integerResult) + fractional;
  }

  // 处理精度
  if (precision >= 0) {
    const factor = Math.pow(10, precision);
    result = Math.round(result * factor) / factor;
  }

  // 处理浮点数精度误差（如 0.30000000000000004 修正）
  if (precision > 0) {
    result = parseFloat(result.toFixed(precision));
  } else if (precision === 0) {
    result = Math.round(result);
  }

  return result;
};

const STORAGE_SUFFIX_UNITS = ['Ki', 'Mi', 'Gi', 'Ti'] as const;

/** 解析带单位后缀的存储/内存字符串，无后缀时按 Byte（历史实现中的 bit）处理 */
function parseStorageValueWithUnit(value: string | number): { numericValue: string; unit: string } {
  const numericValue = String(value);
  for (const unit of STORAGE_SUFFIX_UNITS) {
    if (numericValue.endsWith(unit)) {
      return { numericValue: numericValue.slice(0, -unit.length), unit };
    }
  }
  return { numericValue, unit: 'Byte' };
}

/** 将单值存储/内存（如 16Gi、4096Mi、裸数字）统一转为 Gi 整数 */
export function convertSingleMemoryToGi(value: string | number | undefined | null): number {
  if (!value) return 0;
  const { numericValue, unit } = parseStorageValueWithUnit(value);
  const result = transUnit({ value: numericValue, unit, targetUnit: 'Gi', precision: 0 });
  return Number.isNaN(result) ? 0 : Math.floor(result);
}

export default transUnit;
